import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  App, Button, Input, Popconfirm, Select, Space, Table, Tag, Tooltip, Typography,
} from 'antd';
import {
  CopyOutlined, PlusOutlined, ReloadOutlined, SearchOutlined, StopOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import dayjs from 'dayjs';
import { JobStatusTag } from '@components/StatusTag';
import CreateJobModal from '@components/CreateJobModal';
import {
  cancelJob,
  getPerceptionRefreshCopySource,
  getPerceptionRefreshJobs,
  getPerceptionRefreshTaskType,
  type JobItem,
  type PerceptionRefreshJob,
  type PerceptionRefreshVersionStatus,
} from '@/apis/simulation';

const { Text } = Typography;

const formatTime = (val?: string | null) => (val ? dayjs(val).format('YYYY-MM-DD HH:mm:ss') : '-');

// 可终止的 Job 状态（JobStatusEnum.id）：未开始 3 / 排队中 5 / 编译中 7 / 执行中 10。
const ACTIVE_JOB_STATUSES = new Set([3, 5, 7, 10]);

export const VERSION_STATUS_META: Record<PerceptionRefreshVersionStatus, { color: string; label: string }> = {
  running: { color: 'processing', label: '运行中' },
  succeeded: { color: 'success', label: '全部成功' },
  partial: { color: 'warning', label: '部分成功' },
  failed: { color: 'error', label: '失败' },
};

/**
 * 「数据更新」页的「感知数据（PE_DATA）」页签：感知数据刷写版本列表，一个 Job 一行（一个版本）。
 *
 * 创建复用 CreateJobModal，锁定任务类型为 PE_DATA、模型主版本必填；PE_DATA 的
 * enable_flag=0，不出现在通用任务列表与下拉里，所以入口只在本页签。
 */
export default function PerceptionRefreshPanel() {
  const { message } = App.useApp();
  const [data, setData] = useState<PerceptionRefreshJob[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [page, setPage] = useState({ current: 1, pageSize: 20 });
  const [tagInput, setTagInput] = useState('');
  const [filters, setFilters] = useState<{ groupTag?: string; status?: PerceptionRefreshVersionStatus }>({});
  const [taskType, setTaskType] = useState<{ id: number; name: string } | null>(null);
  const [createOpen, setCreateOpen] = useState(false);
  const [copyRecord, setCopyRecord] = useState<JobItem | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await getPerceptionRefreshJobs({ ...filters, ...page });
      setData(res.list);
      setTotal(res.total);
    } catch (err) {
      console.error('加载感知数据刷写列表失败:', err);
      message.error('加载感知数据刷写列表失败');
    } finally {
      setLoading(false);
    }
  }, [filters, page, message]);

  useEffect(() => {
    void load();
  }, [load]);

  const openCreate = async (record: JobItem | null = null) => {
    setCopyRecord(record);
    if (taskType) {
      setCreateOpen(true);
      return;
    }
    try {
      const tt = await getPerceptionRefreshTaskType();
      setTaskType({ id: tt.taskTypeId, name: tt.name });
      setCreateOpen(true);
    } catch (err) {
      // 任务类型未初始化时后端返回业务错误，axios 拦截器已弹出原因。
      console.error('获取感知数据刷写任务类型失败:', err);
    }
  };

  const handleCopy = async (jobId: number) => {
    try {
      await openCreate(await getPerceptionRefreshCopySource(jobId));
    } catch (err) {
      // 业务错误由 axios 拦截器弹出原因。
      console.error('获取复制源 Job 失败:', err);
    }
  };

  // 终止走通用 /job/cancel（停止 Job，保留产物）；不用 /job/delete，它会清理现场与日志，
  // 已跑完场景的引擎产物还要留给归档 / 重新归档。
  const handleCancel = async (jobId: number) => {
    try {
      await cancelJob(jobId);
      message.success('已终止');
      void load();
    } catch (err) {
      console.error('终止感知数据刷写任务失败:', err);
    }
  };

  const columns: ColumnsType<PerceptionRefreshJob> = [
    { title: 'Job ID', dataIndex: 'jobId', width: 90 },
    { title: 'Job 名称', dataIndex: 'jobName', width: 220, ellipsis: true },
    {
      title: '版本号',
      dataIndex: 'perceptionVersion',
      width: 240,
      render: (v: string) => <Text copyable>{v}</Text>,
    },
    {
      title: '版本状态',
      dataIndex: 'status',
      width: 100,
      render: (s: PerceptionRefreshVersionStatus) => {
        const meta = VERSION_STATUS_META[s];
        return meta ? <Tag color={meta.color}>{meta.label}</Tag> : s;
      },
    },
    {
      title: '场景数',
      key: 'scenes',
      width: 200,
      render: (_, r) => (
        <Tooltip title="成功 / 失败 / 无产物 / 总数">
          <Space size={4}>
            <Text type="success">{r.sceneSucceeded}</Text>/
            <Text type="danger">{r.sceneFailed}</Text>/
            <Text type="warning">{r.sceneNoOutput}</Text>/
            <Text>{r.sceneTotal}</Text>
          </Space>
        </Tooltip>
      ),
    },
    {
      title: 'Job 状态',
      dataIndex: 'jobStatus',
      width: 110,
      render: (_, r) => <JobStatusTag status={r.jobStatus} statusName={r.jobStatusName} />,
    },
    {
      title: '模型主版本',
      dataIndex: 'modelRelease',
      width: 200,
      ellipsis: true,
      // 早期测试 Job（模型主版本曾短暂可选）可能为空
      render: (v?: string | null) => v || '-',
    },
    {
      title: 'BOS 路径',
      dataIndex: 'bosPath',
      width: 320,
      ellipsis: true,
      render: (v: string) => <Text copyable ellipsis={{ tooltip: v }}>{v}</Text>,
    },
    { title: '场景集', dataIndex: 'sceneSetIds', width: 120, ellipsis: true },
    { title: '创建人', dataIndex: 'creator', width: 160, ellipsis: true },
    { title: '创建时间', dataIndex: 'createTime', width: 170, render: formatTime },
    {
      title: '操作',
      key: 'action',
      fixed: 'right',
      width: 230,
      render: (_, r) => (
        <Space size={4}>
          <Link to={`/prediction-update/perception/jobs/${r.jobId}`}>场景明细</Link>
          <Tooltip title="复制该 Job 的发起参数进入创建页面；提交后是新 Job、新版本号">
            <Button size="small" type="link" icon={<CopyOutlined />} onClick={() => void handleCopy(r.jobId)}>
              复制
            </Button>
          </Tooltip>
          {r.jobStatus != null && ACTIVE_JOB_STATUSES.has(r.jobStatus) && (
            <Popconfirm
              title="确认终止"
              description="终止后未跑完的场景不再执行；已跑完的场景照常归档，版本按部分成功 / 失败收尾。"
              onConfirm={() => handleCancel(r.jobId)}
              okText="终止"
              cancelText="不终止"
            >
              <Button size="small" type="link" danger icon={<StopOutlined />}>终止</Button>
            </Popconfirm>
          )}
        </Space>
      ),
    },
  ];

  return (
    <div className="layout-container">
      <Space style={{ width: '100%', justifyContent: 'space-between', marginBottom: 16 }} wrap>
        <Space wrap>
        <Input
          placeholder="版本 tag，如 release.10.12"
          value={tagInput}
          onChange={(e) => setTagInput(e.target.value)}
          onPressEnter={() => {
            setPage((p) => ({ ...p, current: 1 }));
            setFilters((f) => ({ ...f, groupTag: tagInput.trim() || undefined }));
          }}
          style={{ width: 220 }}
          allowClear
        />
        <Select
          placeholder="版本状态"
          allowClear
          style={{ width: 140 }}
          value={filters.status}
          onChange={(status) => {
            setPage((p) => ({ ...p, current: 1 }));
            setFilters((f) => ({ ...f, status }));
          }}
          options={Object.entries(VERSION_STATUS_META).map(([value, m]) => ({ value, label: m.label }))}
        />
        <Button
          icon={<SearchOutlined />}
          onClick={() => {
            setPage((p) => ({ ...p, current: 1 }));
            setFilters((f) => ({ ...f, groupTag: tagInput.trim() || undefined }));
          }}
        >
          查询
        </Button>
        <Button icon={<ReloadOutlined />} onClick={() => void load()}>刷新</Button>
        </Space>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => void openCreate()}>
          创建刷写任务
        </Button>
      </Space>
      <Table
        rowKey="jobId"
        loading={loading}
        columns={columns}
        dataSource={data}
        scroll={{ x: 'max-content' }}
        pagination={{
          current: page.current,
          pageSize: page.pageSize,
          total,
          showSizeChanger: true,
          showTotal: (t) => `共 ${t} 条`,
          onChange: (current, pageSize) => setPage({ current, pageSize }),
        }}
      />
      {taskType && (
        <CreateJobModal
          open={createOpen}
          copyRecord={copyRecord}
          onClose={() => {
            setCreateOpen(false);
            setCopyRecord(null);
          }}
          onSuccess={() => {
            setCreateOpen(false);
            setCopyRecord(null);
            void load();
          }}
          perceptionRefreshTaskType={taskType}
        />
      )}
    </div>
  );
}
